import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Plus, Check, X, Loader2, Sparkles, FolderPlus } from 'lucide-react';
import {
  GROUP_LOGOS,
  groupNameError,
  createCandidateGroup,
  readStoredUser,
} from './candidateGroupUtils';

export default function CandidateGroupSelector({
  candidateGroups = [],
  selectedGroupId = '',
  onSelectGroup,
  mode: controlledMode,
  onModeChange,
  newGroup: controlledNewGroup,
  onNewGroupChange,
  onGroupCreated,
  selectedCompanyId = '',
  companies = [],
  disabled = false,
  compact = false,
  className = '',
}) {
  const { t } = useTranslation();

  // Internal state if uncontrolled
  const [internalMode, setInternalMode] = useState('select');
  const [internalNewGroup, setInternalNewGroup] = useState({
    name: '',
    description: '',
    logo: '🌟',
    company: selectedCompanyId || '',
  });
  const [internalSelectedGroupId, setInternalSelectedGroupId] = useState('');
  const [creating, setCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [createdSuccessMessage, setCreatedSuccessMessage] = useState('');

  // Mode resolution
  const mode = controlledMode !== undefined ? controlledMode : internalMode;
  const setMode = (nextMode) => {
    setErrorMessage('');
    setCreatedSuccessMessage('');
    if (onModeChange) {
      onModeChange(nextMode);
    } else {
      setInternalMode(nextMode);
    }
  };

  // Group resolution
  const currentGroupId = selectedGroupId !== undefined ? String(selectedGroupId) : String(internalSelectedGroupId);
  const handleSelectGroup = (id) => {
    setErrorMessage('');
    setCreatedSuccessMessage('');
    if (onSelectGroup) {
      onSelectGroup(id);
    } else {
      setInternalSelectedGroupId(id);
    }
  };

  // New group data resolution
  const newGroupData = controlledNewGroup !== undefined ? controlledNewGroup : internalNewGroup;
  const updateNewGroup = (patch) => {
    setErrorMessage('');
    const updated = { ...newGroupData, ...patch };
    if (onNewGroupChange) {
      onNewGroupChange(updated);
    } else {
      setInternalNewGroup(updated);
    }
  };

  // Role checks for multi-company
  const userDetails = useMemo(() => readStoredUser(), []);
  const isSuperAdmin = userDetails?.is_superuser === true;
  const adminCompanies = Array.isArray(userDetails?.admin_companies) ? userDetails.admin_companies : [];
  const companyRequired = isSuperAdmin || adminCompanies.length > 1;

  // Selected group object
  const selectedGroup = useMemo(() => {
    if (!currentGroupId) return null;
    return candidateGroups.find((g) => String(g.id) === String(currentGroupId)) || null;
  }, [candidateGroups, currentGroupId]);

  // Handle inline group creation
  const handleInlineCreateGroup = async (e) => {
    if (e) e.preventDefault();
    setErrorMessage('');
    setCreatedSuccessMessage('');

    const validationErr = groupNameError(newGroupData.name);
    if (validationErr) {
      setErrorMessage(validationErr);
      return;
    }

    const companyToUse = newGroupData.company || selectedCompanyId || '';
    if (companyRequired && !companyToUse && companies.length > 0) {
      setErrorMessage(t('candidateGroups.companyRequired', 'Select a company before creating a group.'));
      return;
    }

    setCreating(true);
    try {
      const created = await createCandidateGroup({
        name: newGroupData.name,
        description: newGroupData.description,
        logo: newGroupData.logo,
        companyId: companyToUse,
      });

      if (onGroupCreated) {
        onGroupCreated(created);
      }
      handleSelectGroup(String(created.id));
      updateNewGroup({ name: '', description: '', logo: '🌟' });
      setMode('select');
      setCreatedSuccessMessage(
        t('candidateGroups.createdMessage', 'Group "{{name}}" created and selected.', {
          name: created.name,
        })
      );
    } catch (err) {
      const msg = err.response?.data?.name?.[0]
        || err.response?.data?.detail
        || err.response?.data?.error
        || err.message
        || t('candidateGroups.saveFailed', 'Failed to create candidate group.');
      setErrorMessage(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className={`space-y-2 text-[10.5px] ${className}`} style={{ fontSize: '10.5px' }}>
      {/* Header with Title and Mode Switcher */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <FolderPlus size={13} className="text-blue-600 flex-shrink-0" />
          <span className="font-medium text-gray-800 truncate text-[10.5px]" style={{ fontSize: '10.5px' }}>
            {t('candidateGroups.groupSectionTitle', 'Candidate Group')}
          </span>
          <span className="text-[10.5px] font-semibold uppercase tracking-wider text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0" style={{ fontSize: '10.5px' }}>
            {t('common.optional', 'Optional')}
          </span>
        </div>

        {/* Toggle Pills */}
        <div className="inline-flex p-0.5 bg-gray-100 rounded-lg text-[10.5px] font-medium flex-shrink-0 border border-gray-200/60" style={{ fontSize: '10.5px' }}>
          <button
            type="button"
            disabled={disabled || creating}
            onClick={() => setMode('select')}
            style={{ fontSize: '10.5px' }}
            className={`px-2 py-0.5 rounded-md transition-all flex items-center gap-1 text-[10.5px] ${
              mode === 'select'
                ? 'bg-white text-blue-700 font-semibold shadow-xs'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Users size={11} />
            <span>{t('candidateGroups.selectExisting', 'Select Existing')}</span>
          </button>
          <button
            type="button"
            disabled={disabled || creating}
            onClick={() => setMode('create')}
            style={{ fontSize: '10.5px' }}
            className={`px-2 py-0.5 rounded-md transition-all flex items-center gap-1 text-[10.5px] ${
              mode === 'create'
                ? 'bg-white text-blue-700 font-semibold shadow-xs'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Plus size={11} />
            <span>{t('candidateGroups.createNew', 'Create New')}</span>
          </button>
        </div>
      </div>

      {/* SUCCESS MESSAGE */}
      {createdSuccessMessage && (
        <div className="flex items-center justify-between gap-2 p-2 bg-green-50 border border-green-200 rounded-lg text-[10.5px] text-green-800 animate-in fade-in duration-200" style={{ fontSize: '10.5px' }}>
          <div className="flex items-center gap-1.5">
            <Check size={12} className="text-green-600 flex-shrink-0" />
            <span style={{ fontSize: '10.5px' }}>{createdSuccessMessage}</span>
          </div>
          <button
            type="button"
            onClick={() => setCreatedSuccessMessage('')}
            className="text-green-600 hover:text-green-800 p-0.5 rounded"
          >
            <X size={11} />
          </button>
        </div>
      )}

      {/* MODE 1: SELECT EXISTING GROUP */}
      {mode === 'select' && (
        <div className="space-y-1.5 text-[10.5px]" style={{ fontSize: '10.5px' }}>
          <div className="relative">
            <select
              value={currentGroupId}
              onChange={(e) => {
                if (e.target.value === '__CREATE_NEW__') {
                  setMode('create');
                } else {
                  handleSelectGroup(e.target.value);
                }
              }}
              disabled={disabled || creating}
              style={{ fontSize: '10.5px' }}
              className="w-full bg-white border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-800 px-2.5 py-1.5 text-[10.5px]"
            >
              <option value="" style={{ fontSize: '10.5px' }}>
                {t('candidateGroups.noGroupOption', '— None (Do not assign to a group) —')}
              </option>
              {candidateGroups.map((group) => (
                <option key={group.id} value={String(group.id)} style={{ fontSize: '10.5px' }}>
                  {group.logo ? `${group.logo} ` : ''}{group.name}
                  {group.member_count !== undefined ? ` (${group.member_count})` : ''}
                </option>
              ))}
              <option value="__CREATE_NEW__" style={{ fontSize: '10.5px' }}>
                {t('candidateGroups.optionCreateNew', '+ Create a new group...')}
              </option>
            </select>
          </div>

          {/* Active selection preview banner */}
          {selectedGroup ? (
            <div className="flex items-center justify-between p-2 bg-blue-50/70 border border-blue-200/80 rounded-lg text-[10.5px] text-blue-900" style={{ fontSize: '10.5px' }}>
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="flex h-5 w-5 items-center justify-center rounded bg-white border border-blue-200 text-xs shadow-xs flex-shrink-0">
                  {selectedGroup.logo || '👥'}
                </span>
                <div className="min-w-0">
                  <p className="font-semibold text-blue-950 truncate text-[10.5px]" style={{ fontSize: '10.5px' }}>
                    {selectedGroup.name}
                  </p>
                  {selectedGroup.description && (
                    <p className="text-[10.5px] text-blue-700/80 truncate" style={{ fontSize: '10.5px' }}>
                      {selectedGroup.description}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                disabled={disabled}
                onClick={() => handleSelectGroup('')}
                className="text-blue-600 hover:text-blue-800 p-0.5 hover:bg-blue-100 rounded transition-colors flex-shrink-0 ml-1.5"
                title={t('common.clear', 'Clear selection')}
                aria-label="Clear group selection"
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <p className="text-[10.5px] text-gray-500" style={{ fontSize: '10.5px' }}>
              {t(
                'candidateGroups.optionalHelpText',
                'Candidate(s) will be added to this group upon submission. Leave as None if no group is needed.'
              )}
            </p>
          )}
        </div>
      )}

      {/* MODE 2: CREATE NEW GROUP */}
      {mode === 'create' && (
        <div className="rounded-lg border border-blue-200/80 bg-gradient-to-b from-blue-50/40 to-white p-2.5 space-y-2 animate-in fade-in duration-200 shadow-xs text-[10.5px]" style={{ fontSize: '10.5px' }}>
          <div className="flex items-center justify-between">
            <span className="text-[10.5px] font-semibold text-blue-900 flex items-center gap-1.5" style={{ fontSize: '10.5px' }}>
              <Sparkles size={12} className="text-blue-600" />
              {t('candidateGroups.createNewGroupTitle', 'New Candidate Group')}
            </span>
            <button
              type="button"
              onClick={() => setMode('select')}
              className="text-[10.5px] text-gray-500 hover:text-gray-800 transition-colors"
              style={{ fontSize: '10.5px' }}
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>

          {/* Emoji / Logo Selector */}
          <div>
            <label className="block text-[10.5px] font-medium text-gray-700 mb-1" style={{ fontSize: '10.5px' }}>
              {t('candidateGroups.logoLabel', 'Group Icon / Logo (Optional)')}
            </label>
            <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto p-1 bg-white border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => updateNewGroup({ logo: '' })}
                className={`h-6 w-6 rounded flex items-center justify-center text-[10.5px] border transition-all ${
                  !newGroupData.logo
                    ? 'border-blue-500 bg-blue-100 text-blue-700 font-bold ring-1 ring-blue-400'
                    : 'border-gray-200 text-gray-400 hover:bg-gray-50'
                }`}
                title={t('candidateGroups.noLogo', 'No logo')}
                style={{ fontSize: '10.5px' }}
              >
                —
              </button>
              {GROUP_LOGOS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => updateNewGroup({ logo: emoji })}
                  className={`h-6 w-6 rounded flex items-center justify-center text-sm border transition-all hover:scale-105 ${
                    newGroupData.logo === emoji
                      ? 'border-blue-500 bg-blue-100 ring-2 ring-blue-400'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          {/* Group Name Input */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-[10.5px] font-medium text-gray-700" style={{ fontSize: '10.5px' }}>
                {t('candidateGroups.nameLabel', 'Group Name')} <span className="text-red-500">*</span>
              </label>
              <span className="text-[10.5px] text-gray-400" style={{ fontSize: '10.5px' }}>
                {(newGroupData.name || '').length}/120
              </span>
            </div>
            <input
              type="text"
              value={newGroupData.name || ''}
              onChange={(e) => updateNewGroup({ name: e.target.value })}
              placeholder={t('candidateGroups.namePlaceholder', 'e.g., Campus Hires 2026, Backend Engineers')}
              maxLength={120}
              disabled={disabled || creating}
              style={{ fontSize: '10.5px' }}
              className={`w-full bg-white border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all px-2.5 py-1.5 text-[10.5px] ${
                errorMessage ? 'border-red-400 ring-1 ring-red-300' : 'border-gray-300'
              }`}
            />
          </div>

          {/* Description Input */}
          <div>
            <label className="block text-[10.5px] font-medium text-gray-700 mb-1" style={{ fontSize: '10.5px' }}>
              {t('candidateGroups.descriptionLabel', 'Description (Optional)')}
            </label>
            <input
              type="text"
              value={newGroupData.description || ''}
              onChange={(e) => updateNewGroup({ description: e.target.value })}
              placeholder={t('candidateGroups.descriptionPlaceholder', 'Short description for this group')}
              maxLength={1000}
              disabled={disabled || creating}
              style={{ fontSize: '10.5px' }}
              className="w-full bg-white border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all px-2.5 py-1.5 text-[10.5px]"
            />
          </div>

          {/* Company Selector (if required) */}
          {companyRequired && companies.length > 0 && (
            <div>
              <label className="block text-[10.5px] font-medium text-gray-700 mb-1" style={{ fontSize: '10.5px' }}>
                {t('candidateGroups.company', 'Company')} <span className="text-red-500">*</span>
              </label>
              <select
                value={newGroupData.company || selectedCompanyId || ''}
                onChange={(e) => updateNewGroup({ company: e.target.value })}
                disabled={disabled || creating}
                style={{ fontSize: '10.5px' }}
                className="w-full bg-white border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all px-2.5 py-1.5 text-[10.5px]"
              >
                <option value="" style={{ fontSize: '10.5px' }}>{t('candidateGroups.selectCompany', 'Select a company')}</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id} style={{ fontSize: '10.5px' }}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Error display */}
          {errorMessage && (
            <p className="text-[10.5px] text-red-600 font-medium" style={{ fontSize: '10.5px' }}>
              {errorMessage}
            </p>
          )}

          {/* Create Button */}
          <div className="flex items-center justify-end gap-1.5 pt-1 border-t border-blue-100">
            <button
              type="button"
              disabled={disabled || creating}
              onClick={() => setMode('select')}
              style={{ fontSize: '10.5px' }}
              className="px-2 py-0.5 text-[10.5px] text-gray-600 hover:text-gray-900 rounded-md hover:bg-gray-100 transition-colors"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              disabled={disabled || creating || !(newGroupData.name || '').trim()}
              onClick={handleInlineCreateGroup}
              style={{ fontSize: '10.5px' }}
              className="px-2.5 py-1 text-[10.5px] bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1 shadow-xs"
            >
              {creating ? (
                <>
                  <Loader2 size={11} className="animate-spin" />
                  <span>{t('candidateGroups.creating', 'Creating...')}</span>
                </>
              ) : (
                <>
                  <Check size={11} />
                  <span>{t('candidateGroups.createAndSelectBtn', 'Create & Select')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
